import {
    conversationDetailQueryKey,
    conversationDetailQueryOptions,
    createConversation,
    deleteConversation,
    generateConversationTitle,
    getConversations,
    updateConversation,
} from "@/api";
import type { IAttachmentOption } from "@/api/attachments";
import type { IChatConversationDetail } from "@/api/chat/chat.types";
import {
    OWNED_CONVERSATIONS_KEY,
    SIDEBAR_PAGE_SIZE,
} from "@/api/chat/conversationListKeys";
import { queryClient } from "@/api/queryClient";
import { signalOnceCached } from "@/api/querySignal";
import {
    removeSharedConversation,
    type ISharedConversationListItem,
} from "@/api/shared-conversations";
import {
    sharedConversationQueryOptions,
    sharedConversationsQueryOptions,
} from "@/hooks/useSharedConversation";
import { trackChatSessionStarted, trackWorkflowStarted } from "@/lib/analytics";
import { invalidateConversationLists } from "../utils/invalidateConversationLists";
import {
    fetchQueryFollowingReplacement,
    refreshQueryAfterWrite,
} from "../utils/conversationQueries";
import type { RemoteThreadListAdapter } from "@assistant-ui/react";
import {
    queryOptions,
    type FetchQueryOptions,
    type Query,
    type QueryKey,
} from "@tanstack/react-query";
import { createAssistantStream } from "assistant-stream";
import {
    ChatDocumentLimitExceededError,
    extractChatDocumentLimitError,
} from "@/api/chat/chat-document-limit-error";
import { publishDirectDocumentBudgetRejection } from "@/components/shared/direct-document-budget";

// Namespaced sentinel on RemoteThreadMetadata.externalId. assistant-ui
// treats externalId as an opaque consumer-owned slot (verified against
// @assistant-ui/core dist); using a namespaced string avoids future
// collisions if the library adopts the field for its own purposes.
export const SHARED_EXTERNAL_ID = "cs:shared";

export const UNRESOLVED_EXTERNAL_ID = "cs:unresolved";

// Owner-only by default (no include_shared): the sidebar sources the "Shared
// with me" group separately from getSharedConversations, so pulling shared rows
// in here too would render each shared thread twice. Pinned first, so every pin
// up to the page size is in the one page the sidebar loads.
export const fetchOwnedConversations = ({
    signal,
}: { signal?: AbortSignal } = {}) =>
    getConversations(
        1,
        SIDEBAR_PAGE_SIZE,
        { sort_by: "pinned_then_recently_active" },
        undefined,
        undefined,
        { signal }
    );

// Shared query config so the hook's `useQuery` and `list()`'s `fetchQuery`
// can't drift on key/fetcher/staleTime. `list()` seeds this cache (mirroring
// the shared list's dual-read) so `useThreadPartition` and
// `OwnedThreadListItem` can read each row's `is_pinned` reactively (and toggle
// it optimistically via setQueryData) without re-running `list()`
// (assistant-ui's list is load-once). `refetchOnWindowFocus: false` keeps a
// focus refetch from clobbering an in-flight optimistic pin.
export const ownedConversationsQueryOptions = queryOptions({
    queryKey: OWNED_CONVERSATIONS_KEY,
    queryFn: (context) =>
        fetchOwnedConversations({ signal: signalOnceCached(context) }),
    staleTime: 60_000,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
});

// Default title for freshly-created conversations. Must stay in lockstep
// with the backend's `_DEFAULT_CONVERSATION_TITLES` set in chat.py; both
// the creation default and the auto-title guards key off this value.
export const DEFAULT_CONVERSATION_TITLE = "New Chat";

function isSharedThread(remoteId: string): boolean {
    const detail = queryClient.getQueryData<IChatConversationDetail>(
        conversationDetailQueryKey(remoteId)
    );
    if (detail?.access_mode === "collaborator") return true;
    const share = queryClient.getQueryData(
        sharedConversationQueryOptions(remoteId).queryKey
    );
    if (share?.conversation_id.toString() === remoteId) return true;
    const cache = queryClient.getQueryData(
        sharedConversationsQueryOptions.queryKey
    );
    if (!cache?.items) return false;
    return cache.items.some(
        (item) => item.conversation_id.toString() === remoteId
    );
}

// Join a fetch in flight rather than return the stale data it is replacing,
// but never refetch a list that has settled.
async function readSettledList<TData, TKey extends QueryKey>(
    options: FetchQueryOptions<TData, Error, TData, TKey>
): Promise<TData> {
    const state = queryClient.getQueryState<TData>(options.queryKey);
    if (state?.data !== undefined && state.fetchStatus === "idle") {
        return state.data;
    }
    return fetchQueryFollowingReplacement(queryClient, {
        ...options,
        staleTime: 0,
    });
}

async function resolveShareGrantId(
    remoteId: string
): Promise<ISharedConversationListItem | undefined> {
    const cache = queryClient.getQueryData(
        sharedConversationsQueryOptions.queryKey
    );
    const fromCache = cache?.items.find(
        (it) => it.conversation_id.toString() === remoteId
    );
    if (fromCache) return fromCache;
    try {
        const share = await queryClient.fetchQuery(
            sharedConversationQueryOptions(remoteId)
        );
        return share ?? undefined;
    } catch (error) {
        if (isSharedThread(remoteId)) throw error;
        return undefined;
    }
}

const holdsData = (query: Query) => query.state.data !== undefined;

async function forgetSharedChat(remoteId: string) {
    const shareKey = sharedConversationQueryOptions(remoteId).queryKey;
    await Promise.all([
        queryClient.cancelQueries({
            queryKey: sharedConversationsQueryOptions.queryKey,
            exact: true,
            predicate: holdsData,
        }),
        queryClient.cancelQueries({
            queryKey: shareKey,
            exact: true,
        }),
    ]);
    queryClient.setQueryData(
        sharedConversationsQueryOptions.queryKey,
        (page) =>
            page && {
                ...page,
                items: page.items.filter(
                    (s) => s.conversation_id.toString() !== remoteId
                ),
            }
    );
    queryClient.removeQueries({
        queryKey: conversationDetailQueryKey(remoteId),
        exact: true,
    });
    queryClient.setQueryData(shareKey, null);
    queryClient.invalidateQueries({
        queryKey: sharedConversationsQueryOptions.queryKey,
        exact: true,
    });
    await invalidateConversationLists(queryClient, {
        includeRecent: false,
    });
}

async function writeDetailTitle(remoteId: string, title: string) {
    const options = conversationDetailQueryOptions(remoteId);
    if (queryClient.getQueryState(options.queryKey)?.data === undefined) {
        void refreshQueryAfterWrite(queryClient, options);
        return;
    }
    await queryClient.cancelQueries({
        queryKey: options.queryKey,
        exact: true,
    });
    const state = queryClient.getQueryState(options.queryKey);
    if (state?.data === undefined) return;
    queryClient.setQueryData(
        options.queryKey,
        (cached) => cached && { ...cached, title },
        { updatedAt: state.dataUpdatedAt }
    );
    if (state.isInvalidated) {
        void queryClient.invalidateQueries({
            queryKey: options.queryKey,
            exact: true,
        });
    }
}

/** Cancel older title reads, write the confirmed title into the cached detail, and mark the lists stale. */
async function refreshConversationTitle(remoteId: string, title: string) {
    void refreshQueryAfterWrite(queryClient, ownedConversationsQueryOptions);
    await Promise.all([
        writeDetailTitle(remoteId, title),
        invalidateConversationLists(queryClient),
    ]);
}

let pendingAgentId: number | undefined;
let pendingAgentName: string | undefined;
const recoveredThreadTitles = new Map<string, { title: string }>();

export function setNextAgentId(id: number, name?: string) {
    pendingAgentId = id;
    pendingAgentName = name;
}

export function stageRecoveredThreadTitle(remoteId: string, title: string) {
    const staged = { title };
    recoveredThreadTitles.set(remoteId, staged);
    return () => {
        if (recoveredThreadTitles.get(remoteId) === staged) {
            recoveredThreadTitles.delete(remoteId);
        }
    };
}

export function getStagedRecoveredThreadTitle(remoteId: string) {
    return recoveredThreadTitles.get(remoteId)?.title;
}

// Mirrors `pendingAgentId`; staged by `useStageAttachments`, consumed
// and cleared by `initialize()`.
let pendingAttachments: IAttachmentOption[] | undefined;

export function setNextAttachments(
    attachments: IAttachmentOption[] | undefined
) {
    pendingAttachments = attachments;
}

// Mirrors `pendingAgentId`; staged by a client-scoped ChatSheet while it
// is open (cleared on close so it can't bleed into the global chat),
// consumed and cleared by `initialize()` (Unit 3.3). Assumes a single
// active creation surface: ANY initialize() while a sheet has a client
// staged would adopt the tag (4.3.4 REVIEW). Unreachable today (the
// open sheet owns thread creation), but key the stage by an owner
// before reusing this side-channel for a second concurrent surface.
let pendingClientId: number | undefined;

export function setNextClientId(id: number | undefined) {
    pendingClientId = id;
}

export const apiThreadListAdapter: RemoteThreadListAdapter = {
    async list() {
        // Parallel-fetch: sidebar-mount latency is max(owned, shared) not
        // owned + shared. The shared fetch is routed through fetchQuery so
        // SharedThreadListItem reads from the warmed cache with no extra
        // round-trip. A failing shared fetch is soft; the sidebar hides
        // the Shared group instead of blocking the entire list.
        const [owned, shared] = await Promise.all([
            // Owned is routed through fetchQuery (it used to be a bare call)
            // so the list, including each row's is_pinned, lands in the
            // OWNED_CONVERSATIONS_KEY cache that the partition and row menu
            // read. A failing owned fetch still hard-stops the whole list
            // (no .catch), unchanged from before.
            fetchQueryFollowingReplacement(
                queryClient,
                ownedConversationsQueryOptions
            ),
            queryClient
                .fetchQuery(sharedConversationsQueryOptions)
                .catch((err: unknown) => {
                    // Shared fetch is soft: surface observability in dev so a
                    // disappearing Shared group is traceable, but never block
                    // the owned list.
                    if (import.meta.env.DEV) {
                        console.warn(
                            "[threadList] shared fetch failed, hiding Shared group",
                            err
                        );
                    }
                    return {
                        items: [] as ISharedConversationListItem[],
                    };
                }),
        ]);

        const ownedThreads = owned.items.map((c) => ({
            status: "regular" as const,
            remoteId: c.id.toString(),
            title: c.title,
        }));

        // Shared rows carry SHARED_EXTERNAL_ID so Slice 5's partition
        // selector can bucket them into the "Shared with me" group via a
        // literal-match check. Owned threads intentionally do not set
        // externalId here; `initialize()` still sets a numeric-string
        // externalId for freshly-created owned threads, which is truthy
        // but never equal to SHARED_EXTERNAL_ID; the literal-match keeps
        // those in the Recents bucket.
        const sharedThreads = shared.items.map((s) => ({
            status: "regular" as const,
            remoteId: s.conversation_id.toString(),
            title: s.conversation_title ?? "",
            externalId: SHARED_EXTERNAL_ID,
        }));

        return { threads: [...ownedThreads, ...sharedThreads] };
    },

    async initialize(_threadId: string) {
        const agentId = pendingAgentId;
        const agentName = pendingAgentName;
        pendingAgentId = undefined;
        pendingAgentName = undefined;
        const attachments = pendingAttachments;
        pendingAttachments = undefined;
        const clientId = pendingClientId;
        pendingClientId = undefined;

        let thread: Awaited<ReturnType<typeof createConversation>>;
        try {
            thread = await createConversation({
                title: DEFAULT_CONVERSATION_TITLE,
                ...(agentId !== undefined && { agent_id: agentId }),
                ...(clientId !== undefined && { client_id: clientId }),
                ...(attachments !== undefined && { attachments }),
            });
        } catch (error: unknown) {
            // The staged values were cleared before the await, so a rejected
            // create would otherwise take the user's whole attachment set with
            // it — on the one path where the document limit is most likely to
            // fire. Put them back exactly so the user can drop one and retry.
            // Only the typed limit rejection restores: every other failure
            // keeps its existing behaviour.
            const limitError = await extractChatDocumentLimitError(error);
            if (limitError) {
                pendingAgentId = agentId;
                pendingAgentName = agentName;
                pendingAttachments = attachments;
                pendingClientId = clientId;
                publishDirectDocumentBudgetRejection({
                    limit: limitError.limit,
                });
                throw new ChatDocumentLimitExceededError(limitError.limit);
            }
            throw error;
        }

        trackChatSessionStarted({
            conversation_id: thread.id,
            agent_id: agentId,
        });
        if (agentId !== undefined) {
            trackWorkflowStarted({
                conversation_id: thread.id,
                agent_id: agentId,
                // Staged name is authoritative; fall back to the server-echoed
                // value, then "" so the event always carries a string.
                agent_name: agentName ?? thread.agent_name ?? "",
            });
        }
        await invalidateConversationLists(queryClient);

        return {
            remoteId: thread.id.toString(),
            externalId: thread.id.toString(),
        };
    },

    async rename(remoteId, newTitle) {
        if (isSharedThread(remoteId)) return;
        const updated = await updateConversation(remoteId, {
            title: newTitle,
        });
        await refreshConversationTitle(remoteId, updated.title);
    },

    async archive() {},

    async unarchive() {},

    async delete(remoteId) {
        const detail = queryClient.getQueryData<IChatConversationDetail>(
            conversationDetailQueryKey(remoteId)
        );
        const owned = queryClient.getQueryData(
            ownedConversationsQueryOptions.queryKey
        );
        const isKnownOwner =
            detail?.access_mode !== "collaborator" &&
            (detail?.access_mode === "owner" ||
                owned?.items.some((item) => item.id.toString() === remoteId));
        const share = isKnownOwner
            ? undefined
            : await resolveShareGrantId(remoteId);
        if (share) {
            // Team-visible-ONLY rows reach the viewer via org team-visibility
            // and carry no shared_conversations grant (id === null). There's
            // nothing to unshare and the viewer isn't the owner, so no-op
            // rather than routing a null id into the unshare endpoint or
            // falling through to the owner-delete path. The sidebar also
            // suppresses the Remove affordance for these rows; this is the
            // defense-in-depth backstop (bug insurescope-0j62f).
            if (share.id !== null) {
                await removeSharedConversation(share.id);
                await forgetSharedChat(remoteId);
            }
            return;
        }
        // The owner endpoint answers a non-owner with an anti-enumeration
        // 404, so a shared chat whose grant is gone is forgotten without one.
        if (isSharedThread(remoteId)) {
            await forgetSharedChat(remoteId);
            return;
        }
        await deleteConversation(remoteId);
        void refreshQueryAfterWrite(
            queryClient,
            ownedConversationsQueryOptions,
            (page) =>
                page && {
                    ...page,
                    items: page.items.filter(
                        (c) => c.id.toString() !== remoteId
                    ),
                }
        );
        queryClient.removeQueries({
            queryKey: conversationDetailQueryKey(remoteId),
            exact: true,
        });
        await invalidateConversationLists(queryClient);
    },

    // The detail request starts only after both lists settle: list()
    // replaces threadIds wholesale when it resolves, and the runtime appends
    // fetch()'s thread without checking threadIds again.
    async fetch(remoteId) {
        const owned = await readSettledList(
            ownedConversationsQueryOptions
        ).catch(() => undefined);
        const ownedRow = owned?.items.find((c) => c.id.toString() === remoteId);
        if (ownedRow) {
            return { status: "regular", remoteId, title: ownedRow.title };
        }

        const shared = await readSettledList(
            sharedConversationsQueryOptions
        ).catch(() => undefined);
        const sharedRow = shared?.items.find(
            (s) => s.conversation_id.toString() === remoteId
        );
        if (sharedRow) {
            return {
                status: "regular",
                remoteId,
                title: sharedRow.conversation_title ?? "",
                externalId: SHARED_EXTERNAL_ID,
            };
        }

        try {
            const conversation = await fetchQueryFollowingReplacement(
                queryClient,
                conversationDetailQueryOptions(remoteId)
            );
            return conversation.access_mode === "collaborator"
                ? {
                      status: "regular",
                      remoteId,
                      title: conversation.title,
                      externalId: SHARED_EXTERNAL_ID,
                  }
                : { status: "regular", remoteId, title: conversation.title };
        } catch {
            return {
                status: "regular",
                remoteId,
                externalId: UNRESOLVED_EXTERNAL_ID,
            };
        }
    },

    async generateTitle(remoteId, unstable_messages) {
        const recovered = recoveredThreadTitles.get(remoteId);
        if (recovered !== undefined) {
            recoveredThreadTitles.delete(remoteId);
            await refreshConversationTitle(remoteId, recovered.title);
            return createAssistantStream((controller) => {
                controller.appendText(recovered.title);
                controller.close();
            });
        }

        if (isSharedThread(remoteId)) {
            // Collaborators don't own the thread; the server would 403.
            // Return an empty stream so assistant-ui's auto-title path
            // closes out cleanly without a console error or bogus title.
            return createAssistantStream((controller) => {
                controller.close();
            });
        }

        const firstUserMsg = unstable_messages.find((m) => m.role === "user");

        const prompt = firstUserMsg
            ? Array.isArray(firstUserMsg.content)
                ? firstUserMsg.content
                      .filter((p: { type: string }) => p.type === "text")
                      .map((p: { text: string }) => p.text)
                      .join("")
                : String(firstUserMsg.content)
            : "";

        const updatedThread = await generateConversationTitle(remoteId, {
            prompt,
        });
        await refreshConversationTitle(remoteId, updatedThread.title);

        return createAssistantStream((controller) => {
            controller.appendText(updatedThread.title);
            controller.close();
        });
    },
};
